import React from 'react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Use</h1>
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using The Bela Experience ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 mb-4">
                The Bela Experience is an AI-powered chat application that provides conversational experiences with an AI companion. The service includes both free trial access and premium subscription features.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. AI Disclaimer - Important Notice</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-800 font-semibold mb-2">
                  ⚠️ IMPORTANT: This service uses artificial intelligence technology
                </p>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>You are interacting with an artificial intelligence (AI) system, not a real person.</strong> "Bela" is a fictional character powered by AI technology. All conversations, responses, and interactions are generated by computer algorithms and do not represent the thoughts, feelings, or opinions of any real individual.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Key points to understand:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>No Real Person:</strong> There is no actual person named "Bela" behind the responses. All content is AI-generated.</li>
                <li><strong>Fictional Interactions:</strong> All conversations are simulated and fictional. The AI does not have real emotions, experiences, or relationships.</li>
                <li><strong>No Personal Connection:</strong> The AI cannot form genuine personal relationships or emotional bonds. Any appearance of such connections is part of the programmed simulation.</li>
                <li><strong>Automated Responses:</strong> All messages, including those that may seem personal or intimate, are automatically generated by AI algorithms.</li>
                <li><strong>No Real Memories:</strong> The AI does not retain genuine memories or experiences outside of the technical conversation history.</li>
                <li><strong>Entertainment Purpose:</strong> This service is designed for entertainment and companionship simulation only.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                By using this service, you acknowledge and understand that you are engaging with artificial intelligence technology designed to simulate conversation and companionship. You should not treat interactions as if they were with a real person or develop emotional dependencies on the AI system.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Subscription and Payment Policy</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mb-2">Free Trial</h3>
              <p className="text-gray-700 mb-4">
                We offer a 7-day free trial for new users. You may cancel at any time during the trial period to avoid charges.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-2">No Refunds</h3>
              <p className="text-gray-700 mb-4">
                All subscription fees are final and non-refundable once paid. This policy helps us maintain affordable pricing for all users.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-2">Cancellation</h3>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time to prevent future charges. Your access will continue until the end of your current billing period.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mb-2">Billing Errors</h3>
              <p className="text-gray-700 mb-4">
                If you believe you were charged in error due to a technical issue, please contact us immediately through the feedback form in the application for assistance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. User Conduct</h2>
              <p className="text-gray-700 mb-4">
                You agree to use the service responsibly and in accordance with all applicable laws. You must not:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to the service or other users' accounts</li>
                <li>Transmit content that violates applicable laws, including but not limited to: content involving minors, threats of violence, doxxing or sharing personal information of others without consent, spam, or attempts to distribute malware</li>
                <li>Attempt to circumvent, disable, or interfere with security features of the service</li>
                <li>Interfere with or disrupt the service or servers through excessive requests, automated scripts, or other technical means</li>
                <li>Impersonate others or misrepresent your identity or affiliation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Content and Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All content provided by the service, including AI responses, images, and other materials, is owned by us or our licensors and is protected by copyright and other intellectual property laws.
              </p>
              <p className="text-gray-700 mb-4">
                You retain ownership of any content you provide to the service, but you grant us a license to use, process, and store such content to provide the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Privacy</h2>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service, to understand our practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Disclaimers</h2>
              <p className="text-gray-700 mb-4">
                The service is provided "as is" without any warranties, express or implied. We do not guarantee that the service will be uninterrupted, secure, or error-free. The AI responses are generated automatically and may not always be accurate or appropriate.
              </p>
              <p className="text-gray-700 mb-4">
                The service is currently in beta and may contain bugs or limitations. We are continuously working to improve the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your access to the service at any time, with or without cause or notice. You may also terminate your account at any time by contacting us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the updated terms on our website. Your continued use of the service after such changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Use, please contact us through the feedback form in the application.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
